// Contact source types
export enum ContactSource {
	MANUAL = 'manual',
	LDAP = 'ldap',
	OUTLOOK = 'outlook',
	EXTENSION = 'extension', // For ACD/PBX extensions
}

// Custom extension properties
export interface CustomExtensionProperties {
	signalStrength?: number;
	callDuration?: string;
	lastActivity?: string;
}

// Base contact interface with common properties
export interface BaseContact extends CustomExtensionProperties {
	contactId: string;
	name: string;
	phoneNumber: string;
	source?: ContactSource;

	// Optional common properties
	avatar?: string;

	// Display properties
	initials?: string;
	// User identification
	isCurrentUser?: boolean;
}

// Manual contact - stored in widget settings
export interface ManualContact extends BaseContact {
	source: ContactSource.MANUAL;

	// Additional manual contact properties
	isPublic: boolean;
	created: Date;
	updated?: Date;
}

// External contact - from LDAP, Outlook, etc.
export interface ExternalContact extends BaseContact {
	source: ContactSource.LDAP | ContactSource.OUTLOOK;

	// External contact identifier (DN for LDAP, ID for Outlook, etc.)
	externalId: string;

	// Additional external properties
	lastSynced?: Date;

	// LDAP specific properties (when source is LDAP)
	dn?: string;            // Distinguished Name
	cn: string;             // Full name
	objectGUID?: string;
	telephoneNumber?: string;   // phone number
	mobile?: string;            // mobile number

	// Outlook specific properties (when source is OUTLOOK)
	outlookId?: string;

	// others
	displayName?: string;       // display name
}

// Extension contact - from PBX/ACD system
export interface ExtensionContact extends BaseContact {
	source: ContactSource.EXTENSION;

	id: number;         // phoneId => mapping to contactId
	// phone number
	number?: string

	// Extension specific properties
	/*extensionStatus?: number;
	extensionStatusStartedAt?: number | null;*/

	// Status properties
	status?: number
	statusStartedAt?: number | null
	holdingCall?: boolean
	paused?: boolean
	callQueueAgent?: boolean
	pauseReason?: number
	callParties?: ExtensionParty[]
	heldCallParties?: ExtensionParty[]
	callRecording?: boolean
	callMonitoring?: boolean
	inConference?: boolean
}

export interface ExtensionParty {
	name: string | null;
	number: string | null;
}

export interface CallParty {
	name: string;
	number: string;
}

// Union type for all contact types
export type Contact = ExtensionContact | ManualContact | ExternalContact;

export interface ContactReference {
	contactId: string;
	source: ContactSource;
	order?: number; // Optional explicit ordering - 0-based index
}

export interface ContactGroup {
	groupId: number;
	name: string;
	contacts: ContactReference[];
	created: Date;
	updated?: Date;
}

// Type guards for contact types
export function isExtensionContact(contact: Contact): contact is ExtensionContact {
	return contact.source === ContactSource.EXTENSION;
}

export function isManualContact(contact: Contact): contact is ManualContact {
	return contact.source === ContactSource.MANUAL;
}
